import { AppLoaderEnd, AppLoaderStart, useConfigUIV1 } from '@gimlite/ui-v1';
import { createRoot } from 'react-dom/client';
import OSPSmallLogo from './assets/logos/osp_small';
import { config } from './config';
import { CustomRouter } from './CustomRouter';
import { AddOneMotoristToAnExistingPoolContractWizard } from './wizards/addOneMotoristToAnExistingPoolContract/addOneMotoristToAnExistingPoolContract.wizard';
import { AddSeveralMotoristsToAnExistingPoolContractWizard } from './wizards/addSeveralMotoristsToAnExistingPoolContract/addSeveralMotoristsToAnExistingPoolContract.wizard';
import { ChoiceCreateContractWizard } from './wizards/choiceCreateContract/choiceCreateContract.wizard';
import { CreateNewMasterPoolContractWizard } from './wizards/createNewMasterPoolContract/createNewMasterPoolContract.wizard';
import { CreateNewSingleContractWizard } from './wizards/createNewSingleContract/createNewSingleContract.wizard';
import { CreateNewSubPoolContractWizard } from './wizards/createNewSubPoolContract/createNewSubPoolContract.wizard';
import { ReportTechnicalDeviceFailureWizard } from './wizards/reportTechnicalDeviceFailure/reportTechnicalDeviceFailure.wizard';

const { ENV, VERSION_FRONT, BFF_HOST, BFF_WS_HOST, BFF_TOKEN } = config;

const defaultTheme = 'light';
const color = {
  dark: config.PORTAL === 'operator' ? '#fe1900' : '#dc00dc',
  light: config.PORTAL === 'operator' ? '#c81400' : '#800080',
};

const exceptedPath = ['/verificator'];

const App = () => {
  const {
    ConfigDialog,
    ConfigGQL,
    ConfigI18N,
    ConfigNotification,
    ConfigSSO,
    ConfigWizard,
    ConfigAnt,
    ConfigApp,
    ConfigMenu,
    ConfigTheme,
    ConfigSocket,
    ConfigRoot,
  } = useConfigUIV1();

  return (
    <ConfigRoot>
      <AppLoaderStart
        config={{ item: OSPSmallLogo(), type: 'logo', defaultTheme, color }}
      >
        <ConfigApp
          version={{ env: ENV, front: VERSION_FRONT }}
          favicon={
            config.PORTAL === 'operator'
              ? 'https://cellar-c2.services.clever-cloud.com/yoonite-resources/default/osp-operator.svg'
              : 'https://cellar-c2.services.clever-cloud.com/yoonite-resources/default/osp-tenant.svg'
          }
        >
          <ConfigGQL
            uri={BFF_HOST}
            headers={{
              'x-access-token': config.BFF_TOKEN,
            }}
          >
            <ConfigSSO
              mode={{ type: 'independent' }}
              instance={{
                url: config.SSO_URL,
                realm: config.SSO_REALM,
                clientId: config.SSO_CLIENT_ID,
              }}
              exceptionPath={exceptedPath}
            >
              <ConfigI18N
                queryI18n={
                  exceptedPath.includes(window.location.pathname)
                    ? 'i18nUnconnect'
                    : 'i18n'
                }
              >
                <ConfigDialog>
                  <ConfigSocket uri={BFF_WS_HOST}>
                    <ConfigWizard
                      registry={[
                        {
                          id: 'choiceCreateContract',
                          component: ChoiceCreateContractWizard,
                        },
                        {
                          id: 'addSeveralMotoristsToAnExistingPoolContract',
                          component:
                            AddSeveralMotoristsToAnExistingPoolContractWizard,
                        },
                        {
                          id: 'addOneMotoristToAnExistingPoolContract',
                          component:
                            AddOneMotoristToAnExistingPoolContractWizard,
                        },
                        {
                          id: 'createNewSubPoolContract',
                          component: CreateNewSubPoolContractWizard,
                        },
                        ...(config.PORTAL === 'operator'
                          ? [
                              {
                                id: 'createNewSingleContract',
                                component: CreateNewSingleContractWizard,
                              },
                              {
                                id: 'createNewMasterPoolContract',
                                component: CreateNewMasterPoolContractWizard,
                              },
                              {
                                id: 'reportTechnicalDeviceFailure',
                                component: ReportTechnicalDeviceFailureWizard,
                              },
                            ]
                          : []),
                      ]}
                    >
                      <ConfigMenu>
                        <ConfigNotification>
                          <ConfigTheme
                            color={color}
                            defaultTheme={defaultTheme}
                          >
                            <ConfigAnt>
                              <AppLoaderEnd>
                                <CustomRouter />
                              </AppLoaderEnd>
                            </ConfigAnt>
                          </ConfigTheme>
                        </ConfigNotification>
                      </ConfigMenu>
                    </ConfigWizard>
                  </ConfigSocket>
                </ConfigDialog>
              </ConfigI18N>
            </ConfigSSO>
          </ConfigGQL>
        </ConfigApp>
      </AppLoaderStart>
    </ConfigRoot>
  );
};

const root = createRoot(document.getElementById('root') as HTMLElement);

root.render(<App />);
